import { useCallback, useState, type MutableRefObject } from "react";
import { doc, updateDoc } from "firebase/firestore";
import type { Firestore } from "firebase/firestore";
import type { EditFormState } from "../components/EditModal";
import { toSlug } from "../lib/formatters";

type CloseDeps = {
  db: Firestore;
  stopLockHeartbeat: MutableRefObject<() => void>;
  stopLockWatcher: MutableRefObject<() => void>;
};

const DEFAULT_EDIT_FORM: EditFormState = {
  assetId: undefined,
  blanccoReportUuid: null,
  id: "",
  orderNumber: "",
  manufacturer: "",
  model: "",
  serial: "",
  chargerIncluded: false,
  adapterYesNo: "",
  damageNotes: "",
  customer: "",
  customerKey: "",
  articleNumber: "",
  productType: "",
  productTypeId: "",
  warrantyStartDate: "",
  createdBy: null,
  createdAt: null,
  auditLog: [],
  photos: {},
  completed: false,
  completedAt: null,
  completedBy: null,
  reuse: false,
  resold: false,
  scrap: false,
  grade: "",
  deletePending: false,
  deleteMarkedBy: null,
  deleteMarkedAt: null,
};

export function useEditItem({ db, stopLockHeartbeat, stopLockWatcher }: CloseDeps) {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editIsReadOnly, setEditIsReadOnly] = useState(false);
  const [editInvoiceReportId, setEditInvoiceReportId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>(DEFAULT_EDIT_FORM);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  const closeEdit = useCallback(() => {
    stopLockHeartbeat.current?.();
    stopLockWatcher.current?.();

    const currentId = editId;
    const isReadOnly = editIsReadOnly;

    // Stäng UI direkt
    setIsEditOpen(false);
    setEditId(null);
    setEditIsReadOnly(false);
    setEditInvoiceReportId(null);

    // Släpp låset i bakgrunden med några försök
    if (currentId && !isReadOnly) {
      void (async () => {
        const ref = doc(db, "itInventory", currentId);
        const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            await updateDoc(ref, { lockedBy: null, lockedAt: null } as any);
            return;
          } catch (e: any) {
            if (attempt === maxAttempts) {
              console.warn("Kunde inte släppa låset vid stängning:", e?.message || e);
              return;
            }
            await wait(300 * attempt);
          }
        }
      })();
    }
  }, [db, editId, editIsReadOnly, stopLockHeartbeat, stopLockWatcher]);

  return {
    isEditOpen,
    setIsEditOpen,
    editId,
    setEditId,
    editIsReadOnly,
    setEditIsReadOnly,
    editInvoiceReportId,
    setEditInvoiceReportId,
    editForm,
    setEditForm,
    uploadProgress,
    setUploadProgress,
    closeEdit,
    defaultEditForm: DEFAULT_EDIT_FORM,
    handleEditChange: useCallback(
      (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
      ): void => {
        const t = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        const { name, type, value } = t;
        const v = type === "checkbox" ? (t as HTMLInputElement).checked : value;

        if (name === "productType") {
          const label = String(v || "");
          setEditForm((prev: EditFormState) => ({
            ...prev,
            productType: label,
            productTypeId: label ? toSlug(label) : "",
          }));
        } else {
          setEditForm((prev: EditFormState) => ({ ...prev, [name]: v }));
        }
      },
      []
    ),
  };
}
